import { useRef, useState, type DragEvent } from "react";

import styles from "./UploadStep.module.css";

import { ImportIcon } from "@/components/icons";

interface UploadStepProps {
  file: File | null;
  error: string | null;
  onSelectFile: (file: File) => void;
  onNext: () => void;
}

export function UploadStep({ file, error, onSelectFile, onNext }: UploadStepProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  function handleDrop(event: DragEvent<HTMLButtonElement>) {
    event.preventDefault();
    setDragging(false);
    const dropped = event.dataTransfer.files[0];
    if (dropped) onSelectFile(dropped);
  }

  return (
    <section className={styles.step} aria-labelledby="upload-heading">
      <h2 id="upload-heading" className={styles.heading}>
        Upload prospects
      </h2>
      <p className={styles.intro}>
        Upload a CSV to generate a personalised PDF letter for each row, plus a sheet of mailing
        labels.
      </p>

      <button
        type="button"
        className={dragging ? `${styles.dropzone} ${styles.dragging}` : styles.dropzone}
        onClick={() => inputRef.current?.click()}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        aria-describedby="upload-hint"
      >
        <ImportIcon className={styles.dropIcon} aria-hidden="true" />
        <span className={styles.dropTitle}>Drag &amp; drop your CSV here</span>
        <span id="upload-hint" className={styles.dropHint}>
          or click to browse — max 5 MB
        </span>
        <input
          ref={inputRef}
          type="file"
          accept=".csv"
          className={styles.fileInput}
          onChange={(event) => {
            const chosen = event.target.files?.[0];
            if (chosen) onSelectFile(chosen);
            event.target.value = "";
          }}
        />
      </button>

      {file ? (
        <p className={styles.fileName}>
          Selected: <strong>{file.name}</strong>
        </p>
      ) : null}

      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : null}

      <button type="button" className={styles.primary} disabled={!file} onClick={onNext}>
        Next: map columns
      </button>
    </section>
  );
}
